// Google Analytics 4 (gtag.js) helpers for the Lunde Water client.
//
// The gtag.js loader and the base `gtag('config', …)` call live in
// public/index.html. That base config automatically reports a page_view for the
// very first page load. This is a single-page app, though: React Router changes
// the URL on the client without a full page reload, so Google Analytics never
// sees those in-app navigations on its own. trackPageView() is what we call on
// each route change to report them (see components/analytics/RouteAnalytics).

export const GA_MEASUREMENT_ID = "G-7HPBGT9ZML";

// The loader script in public/index.html attaches gtag() to window. We describe
// its shape here so the rest of the app can call it with full type-safety
// instead of falling back to `any`.
declare global {
  interface Window {
    gtag?: {
      (command: "js", date: Date): void;
      (command: "config", targetId: string, params?: Record<string, unknown>): void;
      (command: "event", eventName: string, params?: Record<string, unknown>): void;
      (command: "set", params: Record<string, unknown>): void;
      (command: "set", fieldName: string, value: unknown): void;
    };
  }
}

/**
 * Report a single-page-app navigation to GA4 as a `page_view` event.
 *
 * Guarded on the presence of window.gtag, so if the tag is blocked (ad/privacy
 * blockers) or simply hasn't finished loading, this is a silent no-op and never
 * throws inside the React tree — analytics must never break the UI.
 *
 * @param path The new in-app path, e.g. "/orders" or "/pay/abc123".
 */
export function trackPageView(path: string): void {
  if (typeof window === "undefined" || typeof window.gtag !== "function") {
    return;
  }

  window.gtag("event", "page_view", {
    send_to: GA_MEASUREMENT_ID,
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  });
}
